import * as styles from "./Countdown.module.scss";
import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { useLingui } from "@lingui/react/macro";

interface Props {
  expireDate: Date;
  label: string;
  variant?: "small";
}

const calculateTimeLeft = (expireDate: Date) => {
  const now = new Date().getTime();
  const target = expireDate.getTime();
  const difference = target - now;

  if (difference <= 0) {
    return null;
  }

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((difference % (1000 * 60)) / 1000)
  };
};

const Countdown: React.FC<Props> = ({ expireDate, label, variant }) => {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(expireDate));
  const { t } = useLingui();

  useEffect(() => {
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft(expireDate);
      if (newTimeLeft) {
        setTimeLeft(newTimeLeft);
      } else {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [expireDate]);

  if (!timeLeft) {
    return null;
  }

  return (
    <React.Fragment>
      <div
        className={clsx(styles.container, {
          [styles.smallContainer]: variant === "small"
        })}
      >
        <h3
          className={clsx(styles.offerEndsLabel, {
            [styles.smallOfferEndsLabel]: variant === "small"
          })}
        >
          {label}
        </h3>
        <div
          className={clsx(styles.countdown, {
            [styles.smallCountdown]: variant === "small"
          })}
        >
          <CountDownItem
            value={timeLeft.days.toString()}
            label={t`DAYS`}
            variant={variant}
          />
          <Divider variant={variant} />
          <CountDownItem
            value={timeLeft.hours.toString()}
            label={t`HOURS`}
            variant={variant}
          />
          <Divider variant={variant} />
          <CountDownItem
            value={timeLeft.minutes.toString()}
            label={t`MINS`}
            variant={variant}
          />
          <Divider variant={variant} />
          <CountDownItem
            value={timeLeft.seconds.toString()}
            label={t`SECS`}
            variant={variant}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

interface CountDownItemProps {
  value: string;
  label: string;
  variant?: "small";
}

const CountDownItem: React.FC<CountDownItemProps> = ({
  value,
  label,
  variant
}) => {
  return (
    <div
      className={clsx(styles.countdownItem, {
        [styles.smallCountdownItem]: variant === "small"
      })}
    >
      <span className={styles.countdownItemNumber}>
        {value.padStart(2, "0")}
      </span>
      <span className={styles.countdownItemLabel}>{label}</span>
    </div>
  );
};

interface DividerProps {
  variant?: "small";
}

const Divider: React.FC<DividerProps> = ({ variant }) => {
  return (
    <div
      className={clsx(styles.divider, {
        [styles.smallDivider]: variant === "small"
      })}
    ></div>
  );
};

export default Countdown;
